/**
 * RequirementPreview Component
 *
 * Displays requirement metadata and specification preview
 * Provides Edit and Execute action buttons
 */

import React from 'react';
import type { RequirementPreviewProps } from '../types/forge';
import styles from '../styles/forge.module.css';

const TIER_LABELS = {
  quick: 'Quick',
  standard: 'Standard',
  complex: 'Complex',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  pending_approval: 'Pending Approval',
  under_review: 'Under Review',
  approved: 'Approved',
  rejected: 'Rejected',
  deferred: 'Deferred',
  in_progress: 'In Progress',
  completed: 'Completed',
  failed: 'Failed',
};

export const RequirementPreview: React.FC<RequirementPreviewProps> = ({
  requirement,
  onEdit,
  onExecute,
}) => {
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className={styles.requirementPreview}>
      <div className={styles.requirementPreviewHeader}>
        <div className={styles.requirementPreviewMeta}>
          <h3 className={styles.requirementPreviewTitle}>{requirement.title}</h3>
          <div className={styles.requirementPreviewBadges}>
            <span className={`${styles.badge} ${styles[`badge--${requirement.tier}`]}`}>
              {TIER_LABELS[requirement.tier]}
            </span>
            <span className={`${styles.badge} ${styles[`badge--status-${requirement.status}`]}`}>
              {STATUS_LABELS[requirement.status]}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.requirementPreviewBody}>
        <div className={styles.requirementPreviewField}>
          <label>Type:</label>
          <span>{requirement.requirementType}</span>
        </div>

        <div className={styles.requirementPreviewField}>
          <label>Description:</label>
          <p>{requirement.description}</p>
        </div>

        <div className={styles.requirementPreviewField}>
          <label>Service:</label>
          <span>{requirement.serviceId}</span>
        </div>

        <div className={styles.requirementPreviewField}>
          <label>Created:</label>
          <span>{formatDate(requirement.createdAt)}</span>
        </div>

        {requirement.updatedAt && requirement.updatedAt !== requirement.createdAt && (
          <div className={styles.requirementPreviewField}>
            <label>Updated:</label>
            <span>{formatDate(requirement.updatedAt)}</span>
          </div>
        )}

        {requirement.pageContext && (
          <div className={styles.requirementPreviewField}>
            <label>Context:</label>
            <span>{requirement.pageContext.title}</span>
          </div>
        )}

        {requirement.prUrl && (
          <div className={styles.requirementPreviewField}>
            <label>Pull Request:</label>
            <a
              href={requirement.prUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.requirementPreviewLink}
            >
              View PR →
            </a>
          </div>
        )}

        {requirement.error && (
          <div className={styles.requirementPreviewError}>
            <label>Error:</label>
            <pre>{requirement.error}</pre>
          </div>
        )}
      </div>

      <div className={styles.requirementPreviewActions}>
        {onEdit && (
          <button
            onClick={onEdit}
            className={`${styles.button} ${styles.buttonSecondary}`}
            disabled={requirement.status === 'in_progress' || requirement.status === 'completed'}
          >
            Edit
          </button>
        )}
        {onExecute && requirement.status === 'approved' && (
          <button
            onClick={onExecute}
            className={`${styles.button} ${styles.buttonPrimary}`}
          >
            Execute
          </button>
        )}
      </div>
    </div>
  );
};

export default RequirementPreview;
